import React, { useState, useEffect, useCallback, useRef } from "react";
import PresetNudger from "./wizard/PresetNudger";
import CropDrawer from "./CropDrawer";

// Same mapping as backend + PresetEditModal — kept in sync
const FIXTURE_MAP = {
    gondola:               { checkTypes: 'GONDOLA_AUDIT,GONDOLA_PLANOGRAM,SHELF_PRESENTATION', promptFile: 'GONDOLA.md' },
    gondola_shelving:      { checkTypes: 'GONDOLA_AUDIT,GONDOLA_PLANOGRAM,SHELF_PRESENTATION', promptFile: 'GONDOLA.md' },
    fridge_door:           { checkTypes: 'COOLROOM_DOORS,FRIDGE_PLANOGRAM', promptFile: 'FRIDGE.md' },
    fridge_wall:           { checkTypes: 'COOLROOM_DOORS,FRIDGE_PLANOGRAM', promptFile: 'FRIDGE.md' },
    freezer_door:          { checkTypes: 'FREEZER_DOORS,FRIDGE_PLANOGRAM', promptFile: 'FREEZER.md' },
    freezer:               { checkTypes: 'FREEZER_DOORS,FRIDGE_PLANOGRAM', promptFile: 'FREEZER.md' },
    ice_cream_freezer:     { checkTypes: 'FREEZER_DOORS,FRIDGE_PLANOGRAM', promptFile: 'FREEZER.md' },
    pie_cabinet:           { checkTypes: 'PIE_CABINET', promptFile: 'PF_CABINET.md' },
    pie_face_cabinet:      { checkTypes: 'PIE_CABINET', promptFile: 'PF_CABINET.md' },
    coffee_station:        { checkTypes: 'COFFEE_STATION', promptFile: 'COFFEE.md' },
    pos_counter:           { checkTypes: 'POS_COUNTER,HEADCOUNT,UNIFORM', promptFile: 'POS_COUNTER.md' },
    counter_register:      { checkTypes: 'POS_COUNTER,HEADCOUNT,UNIFORM', promptFile: 'POS_COUNTER.md' },
    auto_wall:             { checkTypes: 'AUTO_WALL', promptFile: 'AUTO_WALL.md' },
    impulse_fridge:        { checkTypes: 'IMPULSE_FRIDGE', promptFile: 'IMPULSE_FRIDGE.md' },
    standalone_fridge:     { checkTypes: 'IMPULSE_FRIDGE', promptFile: 'IMPULSE_FRIDGE.md' },
    endcap:                { checkTypes: 'ENDCAPS,GONDOLA_PLANOGRAM', promptFile: 'ENDCAPS.md' },
    standalone_display:    { checkTypes: 'STANDALONE_DISPLAY', promptFile: 'STANDALONE_DISPLAY.md' },
    entry_exit:            { checkTypes: 'CUSTOMER_FLOW,DOOR_CLEANLINESS,SIGNAGE', promptFile: 'ENTRY.md' },
    forecourt:             { checkTypes: 'OUTDOOR_CLEANLINESS,SIGNAGE,PUMP_LOCKOUT,GROUNDS', promptFile: 'FORECOURT.md' },
    store_overview:        { checkTypes: 'STORE_OVERVIEW,CLEANLINESS,STOCK_ON_FLOOR', promptFile: 'OVERVIEW.md' },
    confectionery_wall:    { checkTypes: 'GONDOLA_AUDIT,SHELF_PRESENTATION', promptFile: 'STOCK_CHECK.md' },
    confectionery_display: { checkTypes: 'GONDOLA_AUDIT,SHELF_PRESENTATION', promptFile: 'STOCK_CHECK.md' },
    counter_display:       { checkTypes: 'GONDOLA_AUDIT,SHELF_PRESENTATION', promptFile: 'STOCK_CHECK.md' },
    refrigerated_drinks:   { checkTypes: 'COOLROOM_DOORS,FRIDGE_PLANOGRAM', promptFile: 'FRIDGE.md' },
    magazine_rack:         { checkTypes: 'STANDALONE_DISPLAY', promptFile: 'STANDALONE_DISPLAY.md' },
    macaron_cabinet:       { checkTypes: 'MACARON_CABINET', promptFile: 'MACARON_CABINET.md' },
    counter_confectionery: { checkTypes: 'COUNTER_CONFECTIONERY', promptFile: 'COUNTER_CONFECTIONERY.md' },
    dining_seating:        { checkTypes: 'DINING_SEATING', promptFile: 'DINING_SEATING.md' },
    drive_thru:            { checkTypes: 'DRIVE_THRU', promptFile: 'DRIVE_THRU.md' },
    floor_stacks:          { checkTypes: 'FLOOR_STACKS', promptFile: 'FLOOR_STACKS.md' },
};
const FIXTURE_TYPES = Object.keys(FIXTURE_MAP).sort();

function PresetReframeModal({ preset, siteId, onNudge, onConfirm, onSaveEdit, showEditPanel, onClose }) {
    const [imageUrl, setImageUrl] = useState(preset.referenceImageUrl);
    const [azimuth, setAzimuth] = useState(preset.azimuth);
    const [elevation, setElevation] = useState(preset.elevation);
    const [zoom, setZoom] = useState(preset.zoom);
    const [nudging, setNudging] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [error, setError] = useState(null);
    const [moved, setMoved] = useState(false);

    // Step sizes
    const [azStep, setAzStep] = useState(50);
    const [elStep, setElStep] = useState(50);
    const [zmStep, setZmStep] = useState(10);

    // Invert elevation — persists per site in localStorage
    const [invertEl, setInvertEl] = useState(() => {
        try { return localStorage.getItem(`invertEl_${siteId}`) === "1"; } catch { return false; }
    });

    // Classification / naming (only when showEditPanel)
    const [fixtureType, setFixtureType] = useState(preset.fixtureType || "");
    const [fixtureId, setFixtureId] = useState(preset.fixtureId || "");
    const [cropRegion, setCropRegion] = useState(preset.cropRegion || null);
    const [editTab, setEditTab] = useState("classify"); // "classify" | "crop"

    const initDone = useRef(false);

    const toggleInvertEl = () => {
        const next = !invertEl;
        setInvertEl(next);
        try { localStorage.setItem(`invertEl_${siteId}`, next ? "1" : "0"); } catch { /* ignore */ }
    };

    const handleNudge = async (delta, isInit = false) => {
        setNudging(true);
        setError(null);
        try {
            // Include current live position so the API applies deltas to
            // the actual camera position, not the saved DB position.
            // On init (first call), these are the preset's stored values,
            // which matches what the API would read from SiteConfig anyway.
            const payload = {
                ...delta,
                currentAzimuth: azimuth,
                currentElevation: elevation,
                currentZoom: zoom,
            };
            const result = await onNudge(payload);
            setImageUrl(result.imageUrl);
            setAzimuth(result.azimuth);
            setElevation(result.elevation);
            setZoom(result.zoom);
            if (!isInit) setMoved(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setNudging(false);
        }
    };

    // Initial capture: move camera to stored position on mount
    // savePreview: persist captured image for unlocked presets so cards show a preview
    useEffect(() => {
        if (initDone.current) return;
        initDone.current = true;
        handleNudge({ azimuthDelta: 0, elevationDelta: 0, zoomDelta: 0, savePreview: !preset.locked }, true);
    }, []); // eslint-disable-line

    const handleConfirm = async () => {
        setConfirming(true);
        setError(null);
        try {
            await onConfirm({ azimuth, elevation, zoom });
            // If edit panel is active, also save classification/crop
            if (showEditPanel && onSaveEdit) {
                const editData = {};
                if (fixtureType !== (preset.fixtureType || "")) editData.fixtureType = fixtureType;
                if (fixtureId !== (preset.fixtureId || "")) editData.fixtureId = fixtureId;
                if (JSON.stringify(cropRegion) !== JSON.stringify(preset.cropRegion || null)) {
                    editData.cropRegion = cropRegion;
                }
                if (Object.keys(editData).length > 0) {
                    await onSaveEdit(editData);
                }
            }
        } catch (err) {
            setError(err.message);
            setConfirming(false);
        }
    };

    // Keyboard shortcuts — use current step sizes, respect invert
    const elDir = invertEl ? -1 : 1;
    const handleKeyDown = useCallback((e) => {
        if (nudging || confirming) return;
        // Don't intercept keys when typing in input fields
        if (e.target.tagName === "INPUT" || e.target.tagName === "SELECT" || e.target.tagName === "TEXTAREA") return;
        if (e.key === "Escape") { onClose(); return; }
        const delta = { azimuthDelta: 0, elevationDelta: 0, zoomDelta: 0 };
        switch (e.key) {
            case "ArrowLeft":  delta.azimuthDelta = -azStep; break;
            case "ArrowRight": delta.azimuthDelta = azStep; break;
            case "ArrowUp":    delta.elevationDelta = elStep * elDir; break;
            case "ArrowDown":  delta.elevationDelta = -elStep * elDir; break;
            case "+": case "=": delta.zoomDelta = zmStep; break;
            case "-":           delta.zoomDelta = -zmStep; break;
            default: return;
        }
        e.preventDefault();
        handleNudge(delta);
    }, [nudging, confirming, onClose, azStep, elStep, zmStep, elDir]); // eslint-disable-line

    useEffect(() => {
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleKeyDown]);

    const mapping = FIXTURE_MAP[fixtureType];
    const confirmLabel = showEditPanel ? "Confirm & Lock" : "Confirm Position";

    return (
        <div className="modal-overlay reframe-modal-overlay" onClick={onClose}>
            <div className="reframe-modal" onClick={(e) => e.stopPropagation()}
                 style={showEditPanel ? { maxWidth: 1100 } : undefined}>
                <div className="reframe-modal-header">
                    <h3>Re-frame: {preset.presetName}</h3>
                    <span className="reframe-modal-ptz">
                        Az {azimuth} / El {elevation} / Zm {zoom}
                    </span>
                    <button className="modal-close-btn" onClick={onClose}>ESC</button>
                </div>

                <div className="reframe-modal-body">
                    <div className="reframe-modal-image-panel">
                        <div className="reframe-image-container">
                            {imageUrl ? (
                                <img
                                    className="reframe-image"
                                    src={imageUrl}
                                    alt={preset.presetName}
                                />
                            ) : (
                                <div className="framing-no-image">
                                    {nudging ? "Capturing..." : "No image available"}
                                </div>
                            )}
                            {nudging && (
                                <div className="framing-image-overlay">
                                    <div className="spinner" />
                                    <span>Moving camera...</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="reframe-modal-controls">
                        <div className="reframe-control-section">
                            <div className="card-title">PTZ Controls</div>
                            <PresetNudger
                                onNudge={handleNudge}
                                disabled={nudging || confirming}
                                azStep={azStep}
                                elStep={elStep}
                                zmStep={zmStep}
                                onAzStepChange={setAzStep}
                                onElStepChange={setElStep}
                                onZmStepChange={setZmStep}
                                invertEl={invertEl}
                            />
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
                                <label style={{ fontSize: 12, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                                    <input type="checkbox" checked={invertEl} onChange={toggleInvertEl} />
                                    Invert Up/Down
                                </label>
                            </div>
                            <div className="nudge-hint">
                                Arrow keys to pan/tilt, +/- to zoom
                            </div>
                        </div>

                        {/* Classification, naming, crop — only in onboarding mode */}
                        {showEditPanel && (
                            <div className="reframe-control-section" style={{ marginTop: 12 }}>
                                <div style={{ display: "flex", gap: 0, marginBottom: 8 }}>
                                    <button
                                        className={`preset-action-btn ${editTab === "classify" ? "active" : ""}`}
                                        onClick={() => setEditTab("classify")}
                                        style={{
                                            flex: 1, borderRadius: "4px 0 0 4px", fontSize: 12,
                                            background: editTab === "classify" ? "var(--accent)" : undefined,
                                            color: editTab === "classify" ? "#fff" : undefined
                                        }}
                                    >
                                        Classification
                                    </button>
                                    <button
                                        className={`preset-action-btn ${editTab === "crop" ? "active" : ""}`}
                                        onClick={() => setEditTab("crop")}
                                        style={{
                                            flex: 1, borderRadius: "0 4px 4px 0", fontSize: 12,
                                            background: editTab === "crop" ? "var(--accent)" : undefined,
                                            color: editTab === "crop" ? "#fff" : undefined
                                        }}
                                    >
                                        Crop Region
                                    </button>
                                </div>

                                {editTab === "classify" && (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                        <div>
                                            <label style={{ fontSize: 11, color: "var(--text-secondary)", display: "block", marginBottom: 2 }}>
                                                Fixture Type
                                            </label>
                                            <select
                                                className="filter-select"
                                                value={fixtureType}
                                                onChange={(e) => setFixtureType(e.target.value)}
                                                style={{ width: "100%", fontSize: 12 }}
                                            >
                                                <option value="">-- Select --</option>
                                                {FIXTURE_TYPES.map((ft) => (
                                                    <option key={ft} value={ft}>
                                                        {ft.replace(/_/g, " ")}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {mapping && (
                                            <div style={{ fontSize: 11, color: "var(--text-secondary)", padding: "4px 8px", background: "var(--bg-secondary)", borderRadius: 4 }}>
                                                <div>Checks: {mapping.checkTypes}</div>
                                                <div>Prompt: {mapping.promptFile}</div>
                                            </div>
                                        )}
                                        <div>
                                            <label style={{ fontSize: 11, color: "var(--text-secondary)", display: "block", marginBottom: 2 }}>
                                                Description
                                            </label>
                                            <input
                                                type="text"
                                                className="filter-select"
                                                value={fixtureId}
                                                onChange={(e) => setFixtureId(e.target.value)}
                                                placeholder="e.g. Snack Gondola - Bay 1"
                                                style={{ width: "100%", fontSize: 12, boxSizing: "border-box" }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {editTab === "crop" && (
                                    <div>
                                        {imageUrl ? (
                                            <CropDrawer
                                                imageUrl={imageUrl}
                                                initialCrop={cropRegion}
                                                onChange={setCropRegion}
                                            />
                                        ) : (
                                            <div style={{ fontSize: 12, color: "var(--text-secondary)", padding: 12, textAlign: "center" }}>
                                                Nudge the camera first to get an image for cropping.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {error && <div className="wizard-error">{error}</div>}

                        <div className="framing-actions">
                            <button
                                className="action-submit framing-confirm-btn"
                                onClick={handleConfirm}
                                disabled={!moved || confirming || nudging}
                            >
                                {confirming ? "Saving..." : confirmLabel}
                            </button>
                            <button
                                className="action-cancel framing-skip-btn"
                                onClick={onClose}
                                disabled={confirming}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PresetReframeModal;
